import { Component, useEffect, useMemo, useState, type ReactNode } from "react";
import { SDKProvider, useSDK } from "./lib/sdk-context";
import Upload from "./pages/Upload";
import Review from "./pages/Review";
import Enrich from "./pages/Enrich";
import Activity from "./pages/Activity";
import Chains from "./pages/Chains";
import ChainDetail from "./pages/ChainDetail";
import DuplicateChains from "./pages/DuplicateChains";
import MyChains from "./pages/MyChains";
import Changes from "./pages/Changes";
import Tenants from "./pages/Tenants";
import Signups from "./pages/Signups";
import DistributorBridge from "./pages/DistributorBridge";
import GbpAmbiguity from "./pages/GbpAmbiguity";
import AdminMappingsAdSpend from "./pages/AdminMappingsAdSpend";
import AdminIntake from "./pages/AdminIntake";
import BrandMappingsAdSpend from "./pages/BrandMappingsAdSpend";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import OnboardWizard from "./pages/OnboardWizard";
import BrandReview from "./pages/BrandReview";
import BrandMyChains from "./pages/BrandMyChains";
import BrandAccounts from "./pages/BrandAccounts";
import BrandAccountDetail from "./pages/BrandAccountDetail";
import BrandChainDetail from "./pages/BrandChainDetail";
import BrandActivity from "./pages/BrandActivity";
import BrandChanges from "./pages/BrandChanges";
import BrandSignupPage from "./pages/BrandSignupPage";
import GlobalSearch from "./components/GlobalSearch";
import MinimalLayout from "./components/MinimalLayout";
import RequireAdmin from "./components/RequireAdmin";
import RequireSignedIn from "./components/RequireSignedIn";
import { TooltipProvider } from "./components/ui/Tooltip";
import { ToastProvider } from "./components/ui/Toast";
import { applyAccentPalette } from "./lib/design-tokens";
import { setBrandTeamId } from "./lib/api";
import { checkAdminSession } from "./lib/admin-auth";
import { useUnreadChanges } from "./lib/useUnreadChanges";

// parseRoute moved to lib/route.ts so the post-login redirect can share the
// exact same parser (a lookalike copy is how the login loop happened).
// Re-exported because existing tests and callers import it from here.
import { parseRoute } from "./lib/route";
export { parseRoute, ADMIN_ONLY_PAGES } from "./lib/route";

function useHashRoute() {
  const [hash, setHash] = useState<string>(() => window.location.hash || "#/");
  useEffect(() => {
    const on = () => setHash(window.location.hash || "#/");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return hash;
}

class ErrorBoundary extends Component<{ children: ReactNode; sdk: any }, { err: Error | null }> {
  state = { err: null as Error | null };
  static getDerivedStateFromError(err: Error) { return { err }; }
  componentDidCatch(error: Error, info: any) {
    try {
      this.props.sdk?.reportError?.({
        title: "Component crash: StoreSync",
        description: error.message,
        stackTrace: info?.componentStack,
        severity: "high",
        currentPage: window.location.href,
      });
    } catch {}
  }
  render() {
    if (this.state.err) {
      return (
        <div className="max-w-2xl mx-auto px-6 py-16">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong.</h2>
          <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-700 overflow-auto">{this.state.err.message}</pre>
          <button className="btn-primary mt-4" onClick={() => { this.setState({ err: null }); window.location.hash = "#/"; }}>
            Back to home
          </button>
        </div>
      );
    }
    return this.props.children as any;
  }
}

function applyBrandGuide(bg: any | null) {
  const root = document.documentElement.style;
  if (!bg) { applyAccentPalette(null); return false; }
  let touched = false;
  if (bg.colorPrimary) {
    root.setProperty("--brand-primary", bg.colorPrimary);
    applyAccentPalette(bg.colorPrimary);
    touched = true;
  } else {
    applyAccentPalette(null);
  }
  if (bg.colorSecondary) { root.setProperty("--brand-secondary", bg.colorSecondary); touched = true; }
  if (bg.colorAccent) { root.setProperty("--brand-accent", bg.colorAccent); touched = true; }
  if (bg.colorBackground) { root.setProperty("--brand-bg", bg.colorBackground); touched = true; }
  if (bg.fontHeadline) { root.setProperty("--brand-headline", bg.fontHeadline); touched = true; }
  if (bg.fontBody) { root.setProperty("--brand-body", bg.fontBody); touched = true; }
  return touched;
}


// Deprecated top-level pages that a brand session must never see. #/my-chains,
// #/activity and #/changes have brand-scoped equivalents; #/chains is the admin
// canonical directory, so a brand user lands on their own chain list instead.
const DEPRECATED_TO_BRAND: Record<string, string> = {
  "my-chains": "my-chains",
  activity: "activity",
  changes: "changes",
  chains: "chains",
};

/**
 * Given the current route page and the resolved brand slug, return the hash a
 * brand session should be redirected to — or null when no redirect applies
 * (no brand session, or a page that isn't a deprecated top-level route).
 * Pure function so the guard logic is unit-testable without rendering <App/>.
 */
/**
 * Brand pages that folded into the unified Accounts workspace. My Review, My
 * Chains and Mappings stopped being destinations of their own — their
 * functionality lives on one page now — so their old hashes redirect rather
 * than 404 or, worse, keep rendering a second surface that disagrees with the
 * one people actually use. `brand-review` is also the fallback for a bare
 * #/brand/:slug, so this makes Accounts the brand's landing page.
 */
const FOLDED_BRAND_PAGES: Record<string, "accounts" | "account-detail"> = {
  "brand-review": "accounts",
  "brand-my-chains": "accounts",
  "brand-chains": "accounts",
  "brand-mappings-adspend": "accounts",
  "brand-chain-detail": "account-detail",
};

export function resolveBrandRedirect(routePage: string, brand: string | null, param?: string | null): string | null {
  if (!brand) return null;
  const folded = FOLDED_BRAND_PAGES[routePage];
  if (folded === "account-detail" && param) return `#/brand/${brand}/accounts/${encodeURIComponent(param)}`;
  if (folded) return `#/brand/${brand}/accounts`;
  if (!DEPRECATED_TO_BRAND[routePage]) return null;
  // The admin canonical chain directory has no brand equivalent; a brand
  // session lands on its own accounts instead.
  if (routePage === "chains" || routePage === "my-chains") return `#/brand/${brand}/accounts`;
  return `#/brand/${brand}/${DEPRECATED_TO_BRAND[routePage]}`;
}

/**
 * One-shot banner explaining a post-login redirect that did not go where the
 * user clicked (see lib/post-login.ts). Read once and cleared, so it never
 * reappears on a later navigation.
 */
function PostLoginNote() {
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    try {
      const n = sessionStorage.getItem("storesync:post-login-note");
      if (n) {
        setNote(n);
        sessionStorage.removeItem("storesync:post-login-note");
      }
    } catch {
      /* private mode — no note, no banner */
    }
  }, []);
  if (!note) return null;
  return (
    <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start justify-between gap-4">
      <span>{note}</span>
      <button className="text-amber-700 hover:text-amber-900" onClick={() => setNote(null)} aria-label="Dismiss">
        &times;
      </button>
    </div>
  );
}

function Shell() {
  const { sdk, context, brandGuide, ready } = useSDK();
  const hash = useHashRoute();
  const route = useMemo(() => parseRoute(hash), [hash]);
  const brandApplied = useMemo(() => applyBrandGuide(brandGuide), [brandGuide]);
  const { unread, markViewed } = useUnreadChanges();

  // Admin signal for nav + the "Sign out" control.
  // /api/admin/me confirms the httpOnly admin_session cookie set by the unified
  // email+password login (PR #44). The page-level RequireAdmin guard is the real
  // gate; this just hides nav links from non-admins.
  const [adminSession, setAdminSession] = useState(false);
  useEffect(() => {
    let alive = true;
    checkAdminSession().then((ok) => {
      if (alive) setAdminSession(ok);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Brand-session user (storesync_session cookie). Drives brand-aware nav
  // destinations and the brand "Sign out" control. Kept as local state
  // alongside adminSession — the two can be checked independently.
  const [brandSession, setBrandSession] = useState<{ slug: string; role: string; display_name: string } | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j) return;
        if (j.user?.role === "admin") return; // admin handled separately
        if (j.tenant?.slug) {
          setBrandSession({ slug: j.tenant.slug, role: j.user.role, display_name: j.tenant.display_name });
          // Let api.ts send the tenant_id as x-heady-team-id so requireTeam-gated
          // directory routes (Changes/Chains/Activity) resolve for this brand.
          if (j.tenant.tenant_id) setBrandTeamId(j.tenant.tenant_id);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // The brand slug to scope nav + redirects to. A logged-in admin (even one who
  // also has a brand session) keeps the global, unscoped nav.
  const brand = brandSession && !adminSession ? brandSession.slug : null;

  // The public "Sign up" link is for anonymous visitors only: not an admin, and
  // not launched inside Heady OS (no SDK / team context). Signed-in users and
  // SDK-connected brands don't need it.
  const showSignupLink = !adminSession && !sdk && !context;

  const signOut = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
    setAdminSession(false);
    setBrandSession(null);
    setBrandTeamId(null);
    window.location.hash = "#/login";
  };

  if (!ready) {
    return <div className="max-w-2xl mx-auto px-6 py-16 text-gray-600">Loading…</div>;
  }

  // Synchronous redirect guard. A brand session must never mount the legacy
  // admin pages (MyChains/Activity/Changes/Chains): resolve the brand-scoped
  // destination BEFORE rendering and bail with a stub so the legacy component
  // never instantiates. The old useEffect-based redirect raced the first paint —
  // the page mounted, fetched and painted before the effect could fire.
  const brandRedirect = resolveBrandRedirect(route.page, brand, route.param2 ?? route.param ?? null);
  if (brandRedirect) {
    if (window.location.hash !== brandRedirect) {
      window.location.replace(brandRedirect);
    }
    return <div className="max-w-2xl mx-auto px-6 py-16 text-gray-600">Redirecting…</div>;
  }

  // Legacy #/review (Model B "your customer master" queue) is meaningless for an
  // admin session — send admins to the admin mappings page instead. Same
  // synchronous-guard pattern as brandRedirect so <Review/> never mounts.
  if (route.page === "review" && adminSession) {
    const target = "#/admin/mappings/ad-spend";
    if (window.location.hash !== target) {
      window.location.replace(target);
    }
    return <div className="max-w-2xl mx-auto px-6 py-16 text-gray-600">Redirecting…</div>;
  }

  const navItem = (href: string, label: string, badge?: number) => {
    const active = hash === href || (href !== "#/" && hash.startsWith(href));
    return (
      <a
        href={href}
        className={`relative inline-flex items-center px-3 py-2 text-sm font-medium transition-colors ${active ? "text-accent-700" : "text-gray-600 hover:text-gray-900"}`}
      >
        {label}
        {badge !== undefined && badge > 0 && (
          <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
        {active && <span className="absolute inset-x-2 -bottom-px h-0.5 bg-accent-500 rounded-full" />}
      </a>
    );
  };

  return (
    <ErrorBoundary sdk={sdk}>
      <div className="min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/90 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <a href="#/" className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-white text-sm font-bold">S</span>
                <span className="text-base font-semibold text-gray-900">StoreSync</span>
                <span className="hidden sm:inline text-xs text-gray-400 font-normal">Match Console</span>
              </a>
              <nav className="flex items-center gap-1">
                {navItem(
                  brand ? `#/brand/${brand}/onboard` : "#/upload",
                  "Upload Data"
                )}
                {adminSession && navItem("#/chains", "Chains")}
                {adminSession && navItem("#/admin/chains/duplicates", "Duplicates")}
                {brand && navItem(`#/brand/${brand}/accounts`, "Accounts")}
                {adminSession && navItem("#/admin/signups", "Signups")}
                {adminSession && navItem("#/admin/distributors/bridge", "Distributors")}
                {adminSession && navItem("#/admin/mappings/ad-spend", "Ad-spend")}
                {adminSession && navItem("#/admin/intake", "Intake")}
                {brand
                  ? navItem(`#/brand/${brand}/activity`, "Activity")
                  : adminSession && navItem("#/activity", "Activity")}
                {brand
                  ? navItem(`#/brand/${brand}/changes`, "Changes", unread)
                  : adminSession && navItem("#/changes", "Changes", unread)}
                {adminSession && navItem("#/tenants", "Tenants")}
                {adminSession && navItem("#/gbp-ambiguity", "GBP Ambiguity")}
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <GlobalSearch />
              {showSignupLink && (
                <a
                  href="#/brands/signup"
                  className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Sign up
                </a>
              )}
              <span className="hidden md:inline text-xs text-gray-500">
                {context ? `team ${context.teamId.slice(0, 8)}…` : "standalone mode"}
              </span>
              {adminSession || brandSession ? (
                <button
                  type="button"
                  onClick={signOut}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  Sign out
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600">
                  <span className="h-2 w-2 rounded-full" style={{ background: brandApplied ? "var(--accent-500)" : "#cbd5e1" }} />
                  {brandApplied ? "Using your brand guide" : "Using defaults"}
                </span>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
          <PostLoginNote />
          {route.page === "home" && (
            <Home cmHref={brandSession && !adminSession ? `#/brand/${brandSession.slug}/onboard` : "#/upload/cm"} />
          )}
          {route.page === "upload" && <RequireAdmin><UploadData /></RequireAdmin>}
          {(route.page === "upload-cm" || route.page === "upload-wtb") && (
            <RequireAdmin><Upload kind={route.page === "upload-wtb" ? "wtb" : "cm"} /></RequireAdmin>
          )}
          {route.page === "review" && <RequireAdmin><Review /></RequireAdmin>}
          {route.page === "brand-onboard" && <OnboardWizard slug={route.param!} />}
          {route.page === "brand-accounts" && <BrandAccounts slug={route.param!} />}
          {route.page === "brand-account-detail" && (
            <BrandAccountDetail slug={route.param!} chainId={route.param2!} />
          )}
          {route.page === "brand-review" && <BrandReview slug={route.param!} />}
          {(route.page === "brand-my-chains" || route.page === "brand-chains") && <BrandMyChains slug={route.param!} />}
          {route.page === "brand-chain-detail" && <BrandChainDetail slug={route.param!} chainId={route.param2!} />}
          {route.page === "brand-activity" && <BrandActivity slug={route.param!} />}
          {route.page === "brand-changes" && <BrandChanges slug={route.param!} />}
          {route.page === "brand-mappings-adspend" && <BrandMappingsAdSpend slug={route.param!} />}
          {route.page === "brand-upload-cm" && <OnboardWizard slug={route.param!} initialKind="cm" />}
          {route.page === "brand-upload-wtb" && <OnboardWizard slug={route.param!} initialKind="wtb" />}
          {route.page === "brand-upload-adspend" && <OnboardWizard slug={route.param!} initialKind="adspend" />}
          {route.page === "enrich" && <RequireAdmin><Enrich licenseNumber={route.param!} /></RequireAdmin>}
          {route.page === "activity" && <RequireSignedIn><Activity /></RequireSignedIn>}
          {route.page === "chains" && <RequireSignedIn><Chains /></RequireSignedIn>}
          {route.page === "chain-detail" && <RequireSignedIn><ChainDetail chainId={route.param!} /></RequireSignedIn>}
          {route.page === "admin-duplicate-chains" && <RequireAdmin><DuplicateChains /></RequireAdmin>}
          {route.page === "my-chains" && <RequireSignedIn><MyChains /></RequireSignedIn>}
          {route.page === "changes" && <RequireSignedIn><Changes onViewed={markViewed} /></RequireSignedIn>}
          {route.page === "tenants" && <RequireAdmin><Tenants /></RequireAdmin>}
          {route.page === "tenant-detail" && <RequireAdmin><Tenants slug={route.param!} /></RequireAdmin>}
          {route.page === "signups" && <RequireAdmin><Signups /></RequireAdmin>}
          {route.page === "distributor-bridge" && <RequireAdmin><DistributorBridge /></RequireAdmin>}
          {route.page === "admin-mappings-adspend" && <RequireAdmin><AdminMappingsAdSpend /></RequireAdmin>}
          {route.page === "gbp-ambiguity" && <RequireAdmin><GbpAmbiguity /></RequireAdmin>}
          {route.page === "admin-intake" && <RequireAdmin><AdminIntake /></RequireAdmin>}
          {route.page === "done" && <Done />}
        </main>

        <footer className="border-t border-gray-200 py-4 text-center text-xs text-gray-500">
          cannabest-storesync · heady child tool · {ready ? "ready" : "loading"}
        </footer>
      </div>
    </ErrorBoundary>
  );
}

function Home({ cmHref = "#/upload/cm" }: { cmHref?: string }) {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Match your retailer data to the California graph</h2>
        <p className="text-sm text-gray-600 mb-5 max-w-2xl">
          Upload your Customer Master (CM) and your Where-to-Buy (Hoodie) exports. We auto-match licenses against the DCC graph, flag anything ambiguous for review, and regenerate the clean CSV when you're done.
        </p>
        <div className="flex flex-wrap gap-3">
          <a className="btn-primary" href={cmHref}>Upload Customer Master</a>
          <a className="btn-secondary" href="#/upload/wtb">Upload WTB (Hoodie)</a>
          <a className="btn-ghost" href="#/review">Go to review queue</a>
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">How it works</h3>
        <ol className="list-decimal ml-5 space-y-1.5 text-sm text-gray-600">
          <li>Drop a CSV — we auto-detect license / name / website columns.</li>
          <li>Confirm the column mapping, we parse and store the raw rows.</li>
          <li>The match engine runs the same license-first ruleset we use for Raw Garden.</li>
          <li>Review any <span className="font-medium text-gray-800">NEEDS_REVIEW</span> or <span className="font-medium text-gray-800">UNMATCHED</span> rows; supply a website for partially-known licenses to enrich our record.</li>
          <li>Export the final CSV.</li>
        </ol>
      </div>
    </div>
  );
}

export function UploadData() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Upload Data</h2>
        <p className="text-sm text-gray-600 mt-1">Choose the data set you'd like to upload.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Upload Where-to-Buy CSV (Retailer Table)
          </h3>
          <p className="text-sm text-gray-600 flex-1">
            Export from your Where to Buy provider — one row per dispensary location.
          </p>
          <a className="btn-primary mt-4 self-start" href="#/upload/wtb">
            Upload CSV
          </a>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm flex flex-col">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Customer Master CSV</h3>
          <p className="text-sm text-gray-600 flex-1">
            Must have at least 1 row for every Retail Partner "Account" that you wholesale to. (1
            Account can link to multiple retail & delivery licenses.)
          </p>
          <a className="btn-primary mt-4 self-start" href="#/upload/cm">
            Upload CSV
          </a>
        </div>
      </div>
    </div>
  );
}

function Done() {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">All caught up</h2>
      <p className="text-sm text-gray-600 mb-4">No rows left in the review queue. You can re-upload any time.</p>
      <a className="btn-primary" href="#/review">Back to review</a>
    </div>
  );
}

/**
 * Top-level router. Chrome-less pages (#/brands/signup, #/login) render in
 * MinimalLayout — outside the app Shell, so no nav, search, or badges. Everything
 * else renders inside the full Shell.
 */
function Root() {
  const hash = useHashRoute();
  const route = useMemo(() => parseRoute(hash), [hash]);

  if (route.page === "brands-signup") {
    return (
      <MinimalLayout>
        <BrandSignupPage />
      </MinimalLayout>
    );
  }
  if (route.page === "login") {
    return (
      <MinimalLayout>
        <Login />
      </MinimalLayout>
    );
  }
  if (route.page === "forgot-password") {
    return (
      <MinimalLayout>
        <ForgotPassword />
      </MinimalLayout>
    );
  }
  if (route.page === "reset-password") {
    return (
      <MinimalLayout>
        <ResetPassword />
      </MinimalLayout>
    );
  }
  return <Shell />;
}

export default function App() {
  return (
    <SDKProvider>
      <TooltipProvider>
        <ToastProvider>
          <Root />
        </ToastProvider>
      </TooltipProvider>
    </SDKProvider>
  );
}
